import { useEffect, useState } from 'react';
import { Logo } from '@vd/ui';
import { Loader2 } from 'lucide-react';
import { useTheme } from '../app/theme';
import { useTabTitle } from '../lib/tabTitle';

/**
 * Loading page the extension lands on the moment a capture starts. The
 * service worker opens this URL BEFORE the screenshot upload completes,
 * so the React bundle hydration and the upload happen in parallel
 * instead of serially. When the upload succeeds, the SW navigates this
 * tab to /x/ticket?t=<id>.
 *
 * The user spends ~1-3 seconds here on a typical capture (depending on
 * network). The intent is for the wait to feel intentional and active
 * rather than a black void: a centered spinner, the Toado mark, and a
 * rotating tip that tells the user what's happening behind the scenes.
 *
 * If the upload fails the SW closes this tab and surfaces the error
 * via the popup; this page never has to render an error state itself.
 */

const TIPS = [
  'Saving the screenshot to your workspace…',
  'Capturing console, network, and errors…',
  'Redacting any sensitive headers…',
  'Adding the page URL and viewport context…',
  "Toado's getting your bug ready to fix…",
];

export function CapturingPage() {
  // This tab's title (TOADO-905).
  useTabTitle({ parts: ['Capturing…'] });
  const { theme } = useTheme();
  const [tipIndex, setTipIndex] = useState(0);

  // Cycle the tip every 1.6s. The user is here for ~1-3s on a fast
  // capture and up to ~6s on a slow connection, so 1-2 tip swaps are
  // typical. Keeps the wait from feeling stuck.
  useEffect(() => {
    const t = setInterval(() => setTipIndex((i) => (i + 1) % TIPS.length), 1600);
    return () => clearInterval(t);
  }, []);

  const logoSrc = theme === 'dark' ? '/toado%20logo%20white.svg' : '/toado%20logo.svg';

  return (
    <div style={shellStyle}>
      <style>{`@keyframes vd-cap-spin { to { transform: rotate(360deg); } }`}</style>
      <img src={logoSrc} alt="Toado" style={{ height: 28, marginBottom: 24 }} />
      <Loader2 size={36} style={{ color: 'var(--text-secondary)', animation: 'vd-cap-spin 1.2s linear infinite' }} />
      <h1 style={titleStyle}>Capturing your page</h1>
      {/* aria-live="off" so screen readers don't announce every tip
          rotation (1.6s interval = a new tip every breath). The visible
          spinner + heading already convey the loading state for AT
          users; the tips are a sighted-user nicety. */}
      <p style={tipStyle} aria-live="off">{TIPS[tipIndex]}</p>
    </div>
  );
}

const shellStyle: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
  background: 'var(--bg)',
  fontFamily: 'Inter, sans-serif',
  color: 'var(--text)',
};

const titleStyle: React.CSSProperties = {
  fontSize: 18,
  fontWeight: 600,
  margin: '20px 0 4px',
};

const tipStyle: React.CSSProperties = {
  fontSize: 13,
  color: 'var(--text-secondary)',
  margin: 0,
  minHeight: 20,
};
